import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type React from "react";

import { useDebouncedCallback } from "use-debounce";

import type { UseDebouncedInputValueArgs, UseDebouncedInputValueResult } from "./types";

const debounceMs = 200;

/**
 * Coalesces changes to a controlled input into a single trailing `onChange`, while keeping a
 * local draft so the input — and any synchronous reads of its current text — never sees a
 * stale committed value.
 *
 * Supersedes `@services/useDebounce`, whose timer is cleared rather than flushed on unmount and
 * which never adopts a later `value`, and the search-string debounce in
 * `scenes/GrapesEditor/.../useDebouncedInputValue`; both are candidates to migrate here.
 */
export const useDebouncedInputValue = ({
  value,
  onChange,
  onBlur,
  enabled,
}: UseDebouncedInputValueArgs): UseDebouncedInputValueResult => {
  const [draftValue, setDraftValue] = useState(value);
  const inputValue = enabled ? draftValue : value;

  /** Last value handed to `onChange`, to tell our own echo apart from an external write. */
  const lastEmittedRef = useRef(value);
  const previousValueRef = useRef(value);

  const commit = useCallback(
    (next: string) => {
      lastEmittedRef.current = next;
      onChange(next);
    },
    [onChange]
  );
  const debouncedCommit = useDebouncedCallback(commit, debounceMs);

  const onInputChange = useCallback(
    (next: string) => {
      setDraftValue(next);
      if (enabled) {
        debouncedCommit(next);
      } else {
        commit(next);
      }
    },
    [enabled, debouncedCommit, commit]
  );

  // Cancels first so a pending keystroke commit cannot land afterwards and clobber an
  // authoritative value.
  const commitNow = useCallback(
    (next: string) => {
      debouncedCommit.cancel();
      setDraftValue(next);
      commit(next);
    },
    [commit, debouncedCommit]
  );

  // Adopts external writes. Keyed on the prop changing, not on it differing from the draft, so
  // consumers that never echo `value` back keep their text; an adopted value becomes the echo
  // baseline.
  useEffect(() => {
    if (value === previousValueRef.current) {
      return;
    }
    previousValueRef.current = value;
    if (value === lastEmittedRef.current) {
      return;
    }
    lastEmittedRef.current = value;
    debouncedCommit.cancel();
    setDraftValue(value);
  }, [value, debouncedCommit]);

  // Commit pending edits if the input unmounts before the timer fires. A layout cleanup runs
  // before every passive cleanup in the deleted tree, so the edit reaches a parent that
  // debounces it again (and flushes on its own unmount) while that parent is still listening.
  useLayoutEffect(() => {
    return () => {
      debouncedCommit.flush();
    };
  }, [debouncedCommit]);

  const flush = useCallback(() => {
    debouncedCommit.flush();
  }, [debouncedCommit]);

  // Flushes before delegating: Monaco fires `onBlur` on the native `blur` event, ahead of the
  // `focusout` React listens to. Kept referentially stable because Monaco re-registers the
  // listener on every identity change without disposing the previous one.
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;
  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      debouncedCommit.flush();
      onBlurRef.current?.(event);
    },
    [debouncedCommit]
  );

  return useMemo(
    () => ({ inputValue, onInputChange, commitNow, flush, handleBlur }),
    [inputValue, onInputChange, commitNow, flush, handleBlur]
  );
};
